/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Message, Modal } from '@arco-design/web-react';
import { IconEdit } from '@arco-design/web-react/icon';
import { isMobile } from '@/utils/utils';
import { useJoin, useScene } from '@/lib/useCommon';
import {
  buildSceneProfilePayload,
  CompanionDraft,
  CompanionProfile,
  getCompanionDisplayName,
  MAX_COMPANIONS,
  useCompanions,
} from '@/lib/useCompanions';
import ModeSwitch from '@/components/ModeSwitch';
import Apis from '@/app/index';
import { RootState } from '@/store';
import { updateFullScreen, updateShowSubtitle } from '@/store/slices/room';
import CompanionSettingsModal from '@/components/CompanionSettingsModal';
import CompanionDock from '@/components/CompanionDock';
import CompanionAvatar from '@/components/CompanionAvatar';
import BackgroundVideo from '@/components/BackgroundVideo';
import { useBackgroundVideoSound } from '@/lib/useBackgroundVideoSound';
import style from './index.module.less';

const BACKGROUND_OVERLAY = [
  'linear-gradient(180deg, rgba(249, 246, 239, 0.78), rgba(249, 246, 239, 0.16) 36%, rgba(249, 246, 239, 0.18) 65%, rgba(249, 246, 239, 0.96))',
  'linear-gradient(90deg, rgba(249, 246, 239, 0.78), transparent 45%, rgba(249, 246, 239, 0.4))',
].join(', ');

function Antechamber() {
  const dispatch = useDispatch();
  const { scene, sceneConfigMap, interactionMode } = useSelector((state: RootState) => state.room);
  const [joining, rawDispatchJoin] = useJoin();
  const { isScreenMode, isAvatarScene } = useScene();
  const sceneId = scene || 'Custom';
  const currentScene = sceneConfigMap[sceneId] || sceneConfigMap.Custom;
  const pageBg = currentScene?.avatarBgUrl || '';
  const pageBgVideo = currentScene?.avatarBgVideoUrl || '';
  // Video backgrounds play on desktop only; mobile keeps the static image.
  const showBackgroundVideo = !isMobile() && Boolean(pageBgVideo);
  const [bgSoundOn] = useBackgroundVideoSound();
  const [showSettings, setShowSettings] = useState(false);
  const [editingCompanion, setEditingCompanion] = useState<CompanionProfile | null>(null);
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [dockVisible, setDockVisible] = useState(false);
  const [mobileCompanionTrayOpen, setMobileCompanionTrayOpen] = useState(false);
  // Mobile top rail: collapse the 3-segment mode switcher behind a single
  // "模式切换" button so the rail is just two compact buttons and the page
  // below gets more height. Tapping it reveals the switcher in a tray.
  const [mobileModeTrayOpen, setMobileModeTrayOpen] = useState(false);
  // Once a mode is picked, fold the mobile mode-switch tray back up.
  useEffect(() => {
    setMobileModeTrayOpen(false);
  }, [interactionMode]);
  const {
    activeId,
    activeCompanion,
    profiles,
    loaded,
    hasCompanions,
    canAddMore,
    baseScene,
    saveCompanion,
    selectCompanion,
    deleteCompanion,
  } = useCompanions();
  const resolvedCompanion = activeCompanion || profiles[0] || null;
  const openCreateModal = () => {
    if (!canAddMore) {
      Message.info(`最多可创建 ${MAX_COMPANIONS} 个伙伴`);
      return;
    }
    setEditingCompanion(null);
    setShowSettings(true);
  };

  const openEditModal = (profile?: CompanionProfile | null) => {
    setEditingCompanion(profile || resolvedCompanion);
    setShowSettings(true);
  };

  const handleSaveProfile = async (draft: CompanionDraft, editingId?: string) => {
    setSubmittingProfile(true);
    try {
      const result = saveCompanion(draft, editingId);
      if (!result.ok) {
        Message.warning(result.reason === 'limit' ? `最多可创建 ${MAX_COMPANIONS} 个伙伴` : '伙伴保存失败');
        return;
      }
      Message.success(editingId ? '伙伴已更新' : '伙伴已创建');
      setShowSettings(false);
      setEditingCompanion(null);
    } finally {
      setSubmittingProfile(false);
    }
  };

  const handleDeleteProfile = (id: string) => {
    const profile = profiles.find((item) => item.id === id);
    Modal.confirm({
      title: '删除伙伴',
      content: `确认删除“${getCompanionDisplayName(profile)}”吗？`,
      okText: '删除',
      cancelText: '取消',
      onOk: () => {
        deleteCompanion(id);
        if (editingCompanion?.id === id) {
          setShowSettings(false);
          setEditingCompanion(null);
        }
      },
    });
  };

  const syncActiveCompanionToServer = async () => {
    if (!activeCompanion || !baseScene) return true;
    try {
      await Apis.Basic.saveSceneProfile(
        buildSceneProfilePayload(sceneId, baseScene, activeCompanion)
      );
      return true;
    } catch (error) {
      Message.error('同步伙伴配置失败，请稍后重试');
      return false;
    }
  };

  const handleJoinRoom = async () => {
    if (!loaded) {
      Message.info('伙伴加载中，请稍等...');
      return false;
    }
    if (!hasCompanions) {
      Message.info('请先创建伙伴');
      setDockVisible(true);
      return false;
    }
    const synced = await syncActiveCompanionToServer();
    if (!synced) return false;
    dispatch(updateFullScreen({ isFullScreen: !isMobile() && !isScreenMode && !isAvatarScene })); // 初始化
    dispatch(updateShowSubtitle({ isShowSubtitle: true }));

    if (joining) return true;
    const ok = await rawDispatchJoin();
    return ok !== false;
  };

  const modeCopy = {
    title: ['走进它的世界，', '面对面聊一会儿'],
    desc: '选一位伙伴，即刻连线。让声音、表情和流动的风景陪你进入对话。',
  };
  // No longer needed: mobile top rail is now unified with two buttons
  // const mobileCompanionActionLabel = ...

  return (
    <div
      className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}
      style={
        pageBg
          ? {
              backgroundImage: `${BACKGROUND_OVERLAY}, url(${pageBg})`,
            }
          : undefined
      }
    >
      {showBackgroundVideo ? (
        <BackgroundVideo
          src={pageBgVideo}
          poster={pageBg}
          sound={bgSoundOn}
          overlay={BACKGROUND_OVERLAY}
        />
      ) : null}
      <div className={style.hero}>
        {isMobile() ? (
          <div className={style.topRail}>
            <button
              type="button"
              className={`${style.topRailSecondaryBtn} ${
                mobileModeTrayOpen ? style.topRailSecondaryBtnActive : ''
              }`}
              onClick={() =>
                setMobileModeTrayOpen((prev) => {
                  const next = !prev;
                  if (next) setMobileCompanionTrayOpen(false);
                  return next;
                })
              }
            >
              模式切换
            </button>
            <button
              type="button"
              className={`${style.topRailSecondaryBtn} ${
                mobileCompanionTrayOpen ? style.topRailSecondaryBtnActive : ''
              }`}
              onClick={() => {
                if (!loaded) return;
                if (!hasCompanions) {
                  openCreateModal();
                  return;
                }
                setMobileCompanionTrayOpen((prev) => {
                  const next = !prev;
                  if (next) setMobileModeTrayOpen(false);
                  return next;
                });
              }}
            >
              {mobileCompanionTrayOpen ? '收起伙伴' : hasCompanions ? '展开伙伴' : '创建伙伴'}
            </button>
          </div>
        ) : (
          <ModeSwitch className={style.modeSwitch} compact />
        )}
        {isMobile() ? (
          <>
            <div
              className={`${style.mobileModeTray} ${
                mobileModeTrayOpen ? style.mobileModeTrayOpen : ''
              }`}
            >
              <ModeSwitch compact ghost className={style.modeSwitch} />
            </div>
            <div
              className={`${style.mobileCompanionTray} ${
                mobileCompanionTrayOpen ? style.mobileCompanionTrayOpen : ''
              }`}
            >
              <CompanionDock
                visible={mobileCompanionTrayOpen}
                layout="arc"
                dense
                profiles={profiles}
                activeId={activeId}
                canAddMore={canAddMore}
                emptyText="先创建伙伴，再开始沉浸模式"
                onSelect={(id) => {
                  selectCompanion(id);
                  setMobileCompanionTrayOpen(false);
                }}
                onAdd={openCreateModal}
                onDelete={handleDeleteProfile}
              />
            </div>
          </>
        ) : null}
        <div className={style.copy}>
          <div className={style.title}>
            {modeCopy.title[0]}
            <br />
            {modeCopy.title[1]}
          </div>
          <div className={style.desc}>{modeCopy.desc}</div>
        </div>
        <div className={style.aiCardWrap}>
          {resolvedCompanion ? (
            <div className={style.selectedCompanionStage}>
              <button
                type="button"
                className={style.selectedCompanionCard}
                onClick={handleJoinRoom}
                disabled={joining}
                title="即刻连线你的伙伴"
                aria-label="即刻连线你的伙伴"
              >
                {resolvedCompanion.avatar || resolvedCompanion.live2dModelUrl ? (
                  <CompanionAvatar
                    live2dModelUrl={resolvedCompanion.live2dModelUrl}
                    avatar={resolvedCompanion.avatar}
                    portraitFrames={resolvedCompanion.portraitFrames}
                    isTalking={false}
                    fit="contain"
                    zoom={1}
                    objectPosition="center bottom"
                    alt={getCompanionDisplayName(resolvedCompanion)}
                    disableLive2d={isMobile()}
                  />
                ) : (
                  <span>{getCompanionDisplayName(resolvedCompanion).slice(0, 1)}</span>
                )}
              </button>
              <div className={style.selectedCompanionMeta}>
                <div className={style.selectedCompanionNameRow}>
                  <div className={style.selectedCompanionName}>
                    {getCompanionDisplayName(resolvedCompanion)}
                  </div>
                  <button
                    className={style.selectedCompanionSetting}
                    type="button"
                    onClick={() => openEditModal()}
                    disabled={joining}
                    title="编辑角色"
                    aria-label={`编辑角色：${getCompanionDisplayName(resolvedCompanion)}`}
                  >
                    <IconEdit />
                  </button>
                </div>
                <button
                  type="button"
                  className={style.joinButton}
                  onClick={handleJoinRoom}
                  disabled={joining}
                  aria-busy={joining}
                >
                  <span className={style.callSurface} aria-hidden="true">
                    <svg
                      className={`${style.callIcon} ${joining ? style.callIconLoading : ''}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      {joining ? (
                        <path d="M20 12a8 8 0 1 1-8-8" />
                      ) : (
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.69 2.8a2 2 0 0 1-.45 2.11L8.09 9.9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.33 1.84.56 2.8.69A2 2 0 0 1 22 16.92z" />
                      )}
                    </svg>
                  </span>
                  <span>{joining ? '连线中…' : '开始通话'}</span>
                </button>
              </div>
            </div>
          ) : loaded ? (
            <div className={`${style.selectedCompanionStage} ${style.emptySelectedCompanionStage}`}>
              <button
                type="button"
                className={style.selectedCompanionCard}
                onClick={openCreateModal}
                aria-label="创建伙伴"
              >
                <span>+</span>
              </button>
              <div className={style.selectedCompanionMeta}>
                <div className={style.selectedCompanionLabel}>还没有伙伴</div>
                <div className={style.selectedCompanionName}>请先创建一个吧</div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {isMobile() ? null : (
        <div className={style.description}>Powered by 豆包大模型和火山引擎视频云 RTC</div>
      )}
      {isMobile() ? null : (
        <div
          className={style.bottomCompanionArea}
          onMouseEnter={() => setDockVisible(true)}
          onMouseLeave={() => setDockVisible(false)}
        >
          <div className={style.bottomHoverHint}>滑到这里查看伙伴列表</div>
          <CompanionDock
            visible={dockVisible}
            layout="arc"
            profiles={profiles}
            activeId={activeId}
            canAddMore={canAddMore}
            emptyText="还没有伙伴，先创建一个吧"
            showEmptyCard={false}
            onSelect={(id) => {
              selectCompanion(id);
            }}
            onAdd={openCreateModal}
            onDelete={handleDeleteProfile}
          />
        </div>
      )}
      <CompanionSettingsModal
        visible={showSettings}
        profile={editingCompanion}
        submitting={submittingProfile}
        onCancel={() => {
          setShowSettings(false);
          setEditingCompanion(null);
        }}
        onSubmit={handleSaveProfile}
      />
    </div>
  );
}

export default Antechamber;
